// src/App.js

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from './store';

// Eager: small route wrappers, always-mounted helpers, and HomePage (the `/`
// landing that PageSpeed measures — kept in the initial bundle for fastest LCP).
import HomePage from './components/HomePage';
import GatedRoute from './components/GatedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ScrollToTop from './components/ScrollToTop';
import { hasAdminPrivileges } from './utils/permissionsClient';
import { loginSuccess, logout, authCheckCompleted } from './slices/authSlice';
import { clearAuthToken, getStoredToken, getStoredTokenData, storeAuthToken } from './utils/authToken';
import axios from 'axios';
import './landing/rush/rushTheme'; // applies Rush light/dark theme from localStorage on load

// Lazy: every other route is split into its own chunk so it only downloads when
// visited. This keeps the heavy dashboard libs (recharts, MUI DataGrid, slate,
// emoji-mart, xlsx, etc.) out of the initial landing-page payload.
// AppThemeProvider carries MUI — lazy so MUI/Emotion load only for app routes,
// never for the landing/marketing pages (which use plain CSS).
const AppThemeProvider = lazy(() => import('./AppThemeProvider'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const TranslationDashboard = lazy(() => import('./components/TranslationDashboard'));
const RushShell = lazy(() => import('./components/RushShell/Shell'));
const EditProject = lazy(() => import('./components/EditProject'));
const TranslateProject = lazy(() => import('./components/TranslateProject'));
const ProjectDetails = lazy(() => import('./components/ProjectDetails'));
const TranslationProjects = lazy(() => import('./components/TranslationProjects'));
const AllProjects = lazy(() => import('./components/RushPages/AllProjects'));
const MyQueue = lazy(() => import('./components/RushPages/MyQueue'));
const Inbox = lazy(() => import('./components/RushPages/Inbox'));
const RushProjectDetail = lazy(() => import('./components/RushPages/ProjectDetail'));
const RushNewProject = lazy(() => import('./components/RushPages/NewProject'));
const RushTranslationMemory = lazy(() => import('./components/RushPages/TranslationMemory'));
const RushGlossaries = lazy(() => import('./components/RushPages/Glossaries'));
const RushBrandVoice = lazy(() => import('./components/RushPages/BrandVoice'));
const RushAIQuality = lazy(() => import('./components/RushPages/AIQuality'));
const RushResonance = lazy(() => import('./components/RushPages/Resonance'));
const RushPeople = lazy(() => import('./components/RushPages/People'));
const RushSettings = lazy(() => import('./components/RushPages/Settings'));
const RushAccount = lazy(() => import('./components/RushPages/Account'));
const RushEditor = lazy(() => import('./components/RushPages/Editor'));
const GuestApp = lazy(() => import('./components/GuestApp'));
const RushPreview = lazy(() => import('./components/RushPages/Preview'));
const SharedPreview = lazy(() => import('./components/RushPages/Preview/SharedPreview'));
const RushOnboarding = lazy(() => import('./components/RushPages/Onboarding'));
const RushHelp = lazy(() => import('./components/RushPages/Help'));
const RushHelpArticle = lazy(() => import('./components/RushPages/Help/Article'));
const RushReports = lazy(() => import('./components/RushPages/Reports'));
const RushSources = lazy(() => import('./components/RushPages/Sources'));
const RushAutomations = lazy(() => import('./components/RushPages/Automations'));
const RushAdmin = lazy(() => import('./components/RushPages/Admin'));
const NewTranslation = lazy(() => import('./components/NewTranslation'));
const LanguageSettings = lazy(() => import('./components/LanguageSettings'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const DashboardHome = lazy(() => import('./components/DashboardHome'));
const Team = lazy(() => import('./components/TeamPage'));
const GlossaryTerms = lazy(() => import('./components/GlossaryTerms'));
const AdminUsersPage = lazy(() => import('./components/AdminUsersPage'));
const PlatformAdminPage = lazy(() => import('./components/PlatformAdminPage'));
const BlogEditorPage = lazy(() => import('./components/BlogEditorPage'));
const BlogAdminPage = lazy(() => import('./components/BlogAdminPage'));
const AdminDeepLUsagePage = lazy(() => import('./components/AdminDeepLUsagePage'));
const AdminAIUsagePage = lazy(() => import('./components/AdminAIUsagePage'));
const AdminSystemLogsPage = lazy(() => import('./components/AdminSystemLogsPage'));
const UtilitiesPage = lazy(() => import('./components/UtilitiesPage'));
const ChatPage = lazy(() => import('./components/ChatPage'));
const FileManager = lazy(() => import('./components/FileManager'));
const TaskBoard = lazy(() => import('./components/TaskBoard'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const AnalyticsPage = lazy(() => import('./components/AnalyticsPage'));
const AppsMarketplace = lazy(() => import('./components/AppsMarketplace'));
const SupportCenter = lazy(() => import('./components/SupportCenter'));
const AdminSupportManager = lazy(() => import('./components/AdminSupportManager'));
const AdminSupportTickets = lazy(() => import('./components/AdminSupportTickets'));
const JobsBoard = lazy(() => import('./components/JobsBoard'));
const Pricing = lazy(() => import('./components/Pricing'));
const AboutPageWrapper = lazy(() => import('./components/AboutPageWrapper'));
const ContactPageWrapper = lazy(() => import('./components/ContactPageWrapper'));
const FAQPageWrapper = lazy(() => import('./components/FAQPageWrapper'));
const PricingPageWrapper = lazy(() => import('./components/PricingPageWrapper'));
const FeaturesPageWrapper = lazy(() => import('./components/FeaturesPageWrapper'));
const RushCustomersPage = lazy(() => import('./landing/rush/RushCustomersPage'));
const RushBlogPage = lazy(() => import('./landing/rush/RushBlogPage'));
const RushFeatureBrandVoice = lazy(() => import('./landing/rush/RushFeatureBrandVoice'));
const RushFeatureAutomations = lazy(() => import('./landing/rush/RushFeatureAutomations'));
const RushFeatureAiQuality = lazy(() => import('./landing/rush/RushFeatureAiQuality'));
const RushFeaturePreview = lazy(() => import('./landing/rush/RushFeaturePreview'));
const RushFeatureLayoutRisk = lazy(() => import('./landing/rush/RushFeatureLayoutRisk'));
const RushFeatureResonance = lazy(() => import('./landing/rush/RushFeatureResonance'));
const RushFeatureAutopilot = lazy(() => import('./landing/rush/RushFeatureAutopilot'));
const RushCareersPage = lazy(() => import('./landing/rush/RushCareersPage'));
const RushRolePage = lazy(() => import('./landing/rush/RushRolePage'));
const RushLanguagesPage = lazy(() => import('./landing/rush/RushLanguagesPage'));
const RushIntegrationsPage = lazy(() => import('./landing/rush/RushIntegrationsPage'));
const RushSecurityPage = lazy(() => import('./landing/rush/RushSecurityPage'));
const RushPrivacyPage = lazy(() => import('./landing/rush/RushPrivacyPage'));
const RushTermsPage = lazy(() => import('./landing/rush/RushTermsPage'));
const RushGdprPage = lazy(() => import('./landing/rush/RushGdprPage'));
const RushSignInPage = lazy(() => import('./landing/rush/RushSignInPage'));
const RushSignUpPage = lazy(() => import('./landing/rush/RushSignUpPage'));
const RushForgotPasswordPage = lazy(() => import('./landing/rush/RushForgotPasswordPage'));
const RushJoinPage = lazy(() => import('./landing/rush/RushJoinPage'));
const RushTwoFactorChallengePage = lazy(() => import('./landing/rush/RushTwoFactorChallengePage'));
const RushTwoFactorSetupPage = lazy(() => import('./landing/rush/RushTwoFactorSetupPage'));
const RushSsoCallbackPage = lazy(() => import('./landing/rush/RushSsoCallbackPage'));

const DashboardIndex = () => <DashboardHome />;

// Knowledge-base articles now live in the Help center; keep old links working.
const SupportArticleRedirect = () => {
    const { articleSlug } = useParams();
    return <Navigate to={`/dashboard/help/${articleSlug}`} replace />;
};

// Protected route for logged-in users
// Guests are *only* allowed inside /guest/project/<their scoped id> — every
// other authenticated route bounces them there. Non-guests inside /guest/* get
// bounced to /dashboard. This keeps the two shells from leaking into each other.
const ProtectedRoute = ({ element: Component }) => {
    const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);
    const location = useLocation();

    if (isLoading) {
        return null;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    const isGuest = user && user.accountType === 'guest';
    const onGuestRoute = location.pathname.startsWith('/guest/');
    if (isGuest && !onGuestRoute) {
        const dest = user.guestProjectId
            ? `/guest/project/${user.guestProjectId}`
            : '/login'; // no project = expired / revoked; punt back to login
        return <Navigate to={dest} replace />;
    }

    return <Component />;
};

// Guest-only gate for the dedicated guest shell. Bounces logged-out users to
// /login and non-guest users to /dashboard so the route can't be abused.
const GuestRoute = ({ element: Component }) => {
    const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);
    const location = useLocation();

    if (isLoading) return null;
    if (!isAuthenticated) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    if (!user || user.accountType !== 'guest') {
        return <Navigate to="/dashboard" replace />;
    }
    return <Component />;
};

const App = () => {
    const dispatch = useDispatch();
    const { token, user } = useSelector((state) => state.auth);
    const [socket, setSocket] = useState(null);

    // Restore session
    useEffect(() => {
        const savedToken = getStoredToken();
        if (!savedToken) {
            dispatch(authCheckCompleted());
            return;
        }

        axios
            .get('/api/users/me', {
                headers: { Authorization: `Bearer ${savedToken}` },
            })
            .then((res) => {
                dispatch(loginSuccess({ token: savedToken, user: res.data }));
            })
            .catch(() => {
                clearAuthToken();
                dispatch(logout());
            });
    }, [dispatch]);

    // Socket for presence. socket.io-client is dynamically imported so it stays
    // out of the initial bundle — the landing/marketing pages never open a
    // socket, only authenticated sessions do.
    useEffect(() => {
        if (!token || !user) return;
        const existing = getStoredTokenData();
        if (!existing || existing.token !== token) {
            storeAuthToken(token, existing?.rememberMe ?? true);
        }

        let sock;
        let cancelled = false;
        import('socket.io-client').then(({ io }) => {
            if (cancelled) return;
            sock = io('/', { transports: ['websocket', 'polling'] });
            sock.emit('join', user._id);
            sock.on('statusUpdate', ({ userId, status }) => {
                // handle if needed
            });
            // Expose the socket globally so feature surfaces (Editor comments,
            // Inbox, Sidebar badge) can subscribe without prop-drilling. Only
            // sign-in pages used to set this — magic-link / guest sessions
            // would land here with __SOCKET__ undefined, breaking realtime UX.
            if (typeof window !== 'undefined') window.__SOCKET__ = sock;
            setSocket(sock);
        });

        return () => {
            cancelled = true;
            if (sock) {
                sock.disconnect();
                if (typeof window !== 'undefined' && window.__SOCKET__ === sock) {
                    window.__SOCKET__ = null;
                }
            }
            setSocket(null);
        };
    }, [token, user]);

    return (
            <Router>
                <ScrollToTop />
                <Suspense fallback={null}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPageWrapper />} />
                    <Route path="/contact" element={<ContactPageWrapper />} />
                    <Route path="/faqs" element={<FAQPageWrapper />} />
                    <Route path="/pricing" element={<PricingPageWrapper />} />
                    <Route path="/features" element={<FeaturesPageWrapper />} />
                    <Route path="/features/brand-voice" element={<RushFeatureBrandVoice />} />
                    <Route path="/features/automations" element={<RushFeatureAutomations />} />
                    <Route path="/features/ai-quality" element={<RushFeatureAiQuality />} />
                    <Route path="/features/preview" element={<RushFeaturePreview />} />
                    <Route path="/features/layout-risk" element={<RushFeatureLayoutRisk />} />
                    <Route path="/features/resonance" element={<RushFeatureResonance />} />
                    <Route path="/features/autopilot" element={<RushFeatureAutopilot />} />
                    <Route path="/customers" element={<RushCustomersPage />} />
                    <Route path="/blog" element={<RushBlogPage />} />
                    <Route path="/careers" element={<RushCareersPage />} />
                    <Route path="/roles/:slug" element={<RushRolePage />} />
                    <Route path="/languages" element={<RushLanguagesPage />} />
                    <Route path="/integrations" element={<RushIntegrationsPage />} />
                    <Route path="/security" element={<RushSecurityPage />} />
                    <Route path="/privacy" element={<RushPrivacyPage />} />
                    <Route path="/terms" element={<RushTermsPage />} />
                    <Route path="/gdpr" element={<RushGdprPage />} />
                    <Route path="/forgot-password" element={<RushForgotPasswordPage />} />
                    <Route path="/register" element={<RushSignUpPage />} />
                    <Route path="/login" element={<RushSignInPage />} />
                    <Route path="/join/:token" element={<RushJoinPage />} />
                    <Route path="/two-factor/challenge" element={<RushTwoFactorChallengePage />} />
                    <Route path="/two-factor/setup" element={<RushTwoFactorSetupPage />} />
                    <Route path="/sso/callback" element={<RushSsoCallbackPage />} />

                    {/* App routes — everything that uses MUI lives under this pathless
                        layout so the MUI theme (and the MUI/Emotion bundle) loads only
                        here, never on the marketing/landing routes above. */}
                    <Route element={<AppThemeProvider><Outlet /></AppThemeProvider>}>
                    <Route path="/shared/:token" element={<SharedPreview />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Editor — own chrome, no main sidebar */}
                    <Route
                        path="/editor/:id"
                        element={<ProtectedRoute element={RushEditor} />}
                    />
                    {/* Guest shell — minimal chrome, single scoped project */}
                    <Route
                        path="/guest/project/:id"
                        element={<GuestRoute element={GuestApp} />}
                    />
                    <Route
                        path="/onboarding"
                        element={<ProtectedRoute element={RushOnboarding} />}
                    />

                    {/* Protected dashboard and nested */}
                    <Route
                        path="/dashboard/*"
                        element={<ProtectedRoute element={RushShell} />}
                    >
                        <Route path="" element={<DashboardIndex />} />
                        <Route path="projects" element={<AllProjects />} />
                        <Route path="queue" element={<MyQueue />} />
                        <Route path="inbox" element={<Inbox />} />
                        <Route path="translation-memory" element={<RushTranslationMemory />} />
                        <Route path="glossaries" element={<RushGlossaries />} />
                        <Route path="brand-voice" element={<RushBrandVoice />} />
                        <Route path="ai-quality" element={<RushAIQuality />} />
                        <Route path="resonance" element={<RushResonance />} />
                        <Route path="people" element={<RushPeople />} />
                        <Route path="settings" element={<RushSettings />} />
                        <Route path="account" element={<RushAccount />} />
                        <Route path="preview" element={<RushPreview />} />
                        <Route path="help" element={<RushHelp />} />
                        <Route path="help/:articleSlug" element={<RushHelpArticle />} />
                        <Route path="reports" element={<RushReports />} />
                        <Route path="sources" element={<RushSources />} />
                        <Route path="automations" element={<RushAutomations />} />
                        <Route path="admin" element={<RushAdmin />} />
                        <Route path="new-translation" element={<RushNewProject />} />
                        <Route path="new-translation-classic" element={<NewTranslation />} />
                        <Route path="projects/:id/edit" element={<EditProject />} />
                        <Route path="projects/:id" element={<RushProjectDetail />} />
                        <Route path="projects/:id/translate" element={<TranslateProject />} />
                        <Route path="projects/:id/tasks" element={<TaskBoard />} />
                        <Route path="language-settings" element={<LanguageSettings />} />
                        <Route path="account-settings" element={<AccountSettings />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route
                            path="team"
                            element={<GatedRoute element={Team} requiredFeature="teamAccess" />}
                        />
                        <Route path="apps" element={<AppsMarketplace />} />
                        <Route path="support" element={<SupportCenter />} />
                        <Route path="support/:articleSlug" element={<SupportArticleRedirect />} />
                        <Route
                            path="chat/:userId?"
                            element={<GatedRoute element={() => <ChatPage socket={window.__SOCKET__} />} requiredFeature="chatAccess" />}
                        />
                        <Route path="file-manager" element={<FileManager />} />
                        <Route path="jobs" element={<JobsBoard />} />
                        <Route
                            path="analytics"
                            element={<GatedRoute element={AnalyticsPage} requiredFeature="analytics" />}
                        />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="glossary/:glossaryId/terms" element={<GlossaryTerms />} />
                        <Route
                            path="admin/deepl-usage"
                            element={<ProtectedAdminRoute element={AdminDeepLUsagePage} />}
                        />
                        <Route
                            path="admin/logs"
                            element={<ProtectedAdminRoute element={AdminSystemLogsPage} />}
                        />
                        <Route
                            path="admin/ai-usage"
                            element={<ProtectedAdminRoute element={AdminAIUsagePage} />}
                        />
                        <Route
                            path="admin/utilities"
                            element={<ProtectedAdminRoute element={UtilitiesPage} />}
                        />
                        <Route
                            path="admin/support"
                            element={<ProtectedAdminRoute element={AdminSupportManager} />}
                        />
                        <Route
                            path="admin/support-tickets"
                            element={<ProtectedAdminRoute element={AdminSupportTickets} />}
                        />
                        <Route
                            path="admin/users"
                            element={<ProtectedAdminRoute element={AdminUsersPage} />}
                        />
                        <Route
                            path="admin/platform"
                            element={<ProtectedAdminRoute element={PlatformAdminPage} />}
                        />
                        <Route
                            path="admin/blog"
                            element={<ProtectedAdminRoute element={BlogAdminPage} />}
                        />
                        <Route
                            path="admin/blog-editor"
                            element={<ProtectedAdminRoute element={BlogEditorPage} />}
                        />
                    </Route>
                    </Route>
                </Routes>
                </Suspense>
            </Router>
    );
};

// Wrap App in Redux Provider
const RootApp = () => (
    <Provider store={store}>
        <App />
    </Provider>
);

export default RootApp;
